import { z } from "zod";
import { correctionDate as stockholmDay } from "./preconsult-summary";

// TES-279 (spec §1, beslut 2026-07-03) — the canonical, STORED patient
// journey state. One field answers "where is this patient in the Ella
// journey"; the previous truth was scattered across three state machines
// (Treatment, PrescriptionCycle, Meeting) plus two runtime derivations
// that never reconciled.
//
// Decisions this encodes:
//   - journeyState is the stored source of truth (14 states below);
//   - treatmentPhase is DERIVED from treatment/cycle timestamps;
//   - currentMedicalCycleDay is DERIVED from the active cycle's start.
//
// The 12 linear states form a monotonic ladder (JOURNEY_STATE_ORDINAL):
// the API only ever advances FORWARD along it, so late/re-delivered
// webhooks (Daily, Stripe) can never regress a patient. `paused` and
// `ended` are OVERLAY states enterable from any live state; `paused`
// resumes into treatment-era states, `ended` re-enters only at the
// re-onboarding points (returning patient).
export const JOURNEY_STATES = [
  "lead_started",
  "eligibility_completed",
  "meeting_booked",
  "health_declaration_completed",
  // The prescription-transfer branch. She has submitted
  // F-02 and F-07 (prescription evidence + blood pressure/resting pulse) and
  // is waiting for a doctor to review it. It sits where the consultation sits
  // for a new treatment, because it is the same point in the journey reached
  // a different way: everything before it is the patient's account of
  // herself, everything after is a clinician's decision about her.
  //
  // A new-treatment patient never enters it; the ladder allows skipping
  // forward, so she advances straight past it to doctor_visit_completed.
  "prescription_transfer_review_pending",
  "doctor_visit_completed",
  "labs_required",
  "labs_completed",
  "treatment_decision_ready",
  "treatment_started",
  "active_treatment",
  "maintenance",
  "paused",
  "ended",
] as const;

export const journeyStateSchema = z.enum(JOURNEY_STATES);
export type JourneyState = z.infer<typeof journeyStateSchema>;

// Ordinal position on the linear ladder; the overlay states sit outside it.
export const JOURNEY_STATE_ORDINAL: Record<JourneyState, number> = {
  lead_started: 0,
  eligibility_completed: 1,
  meeting_booked: 2,
  health_declaration_completed: 3,
  prescription_transfer_review_pending: 4,
  doctor_visit_completed: 5,
  labs_required: 6,
  labs_completed: 7,
  treatment_decision_ready: 8,
  treatment_started: 9,
  active_treatment: 10,
  maintenance: 11,
  paused: -1,
  ended: -1,
};

const LINEAR_STATES = JOURNEY_STATES.filter(
  (s) => JOURNEY_STATE_ORDINAL[s] >= 0,
);

// Transition table (same shape as prescription-cycle's cycleTransitions).
// Linear states may advance to any LATER linear state (steps can be
// legitimately skipped — e.g. a returning patient books before the lead
// funnel finishes) or to an overlay state. `paused` resumes to any live
// treatment-era state or ends. `ended` is terminal for the ordinary flow
// but RE-ENTERABLE at the real re-onboarding points — the product supports
// a patient returning after a rejected/ended treatment (clean intake
// re-submit → doctor re-approval), and the Treatment machinery revives
// ended → active; without these targets a returning patient would be
// wedged in `ended` forever while actively in treatment (PR #95 review).
export const journeyTransitions: Record<JourneyState, JourneyState[]> =
  Object.fromEntries([
    ...LINEAR_STATES.map((s) => [
      s,
      [
        ...LINEAR_STATES.filter(
          (t) => JOURNEY_STATE_ORDINAL[t] > JOURNEY_STATE_ORDINAL[s],
        ),
        "paused",
        "ended",
      ],
    ]),
    [
      "paused",
      ["treatment_started", "active_treatment", "maintenance", "ended"],
    ],
    [
      "ended",
      [
        "meeting_booked",
        "health_declaration_completed",
        "treatment_started",
        "active_treatment",
      ],
    ],
  ]) as Record<JourneyState, JourneyState[]>;

export function canAdvanceJourney(
  from: JourneyState,
  to: JourneyState,
): boolean {
  return journeyTransitions[from]?.includes(to) ?? false;
}

// --- derived: treatment phase ----------------------------------------------
// Derived per beslut — NOT stored. `activationAt` is the ACTIVE cycle's
// prescribedAt (Treatment.startedAt is intake-time and misleadingly early).
export const TREATMENT_PHASES = [
  "pre_treatment",
  "start_phase",
  "dose_adjustment_phase",
  "active_weight_loss",
  "stabilisation",
  "maintenance",
  "paused",
  "ended",
] as const;
export const treatmentPhaseSchema = z.enum(TREATMENT_PHASES);
export type TreatmentPhase = z.infer<typeof treatmentPhaseSchema>;

// Month boundaries for the time-derived phases, counted from first
// activation. Placeholder clinical calibration — the boundaries are data,
// not structure, so tuning them later is a one-line change.
const START_PHASE_MONTHS = 1;
const DOSE_ADJUSTMENT_MONTHS = 3;
const ACTIVE_WEIGHT_LOSS_MONTHS = 9;
// Exported: the TES-280 maintenance sweep advances journeyState
// active_treatment → maintenance at this same boundary, so the stored
// state and the derived phase agree about "maintenance" (up to the sweep's
// daily cadence — the derived phase flips continuously, the stored state
// on the next 06:20 run).
export const MAINTENANCE_AFTER_MONTHS = 12;
const STABILISATION_MONTHS = MAINTENANCE_AFTER_MONTHS;
// The same synthetic month deriveTreatmentPhase divides by — exported so the
// sweep's cutoff uses IDENTICAL arithmetic instead of calendar setMonth()
// (which is 365/366 days vs 12 × 30.44 = 365.28 and would disagree at the
// boundary).
const MS_PER_PHASE_MONTH = 30.44 * 24 * 60 * 60 * 1000;

// A first activation at/before this instant has crossed the maintenance
// boundary per deriveTreatmentPhase.
export function maintenanceCutoff(now: Date): Date {
  return new Date(
    now.getTime() - MAINTENANCE_AFTER_MONTHS * MS_PER_PHASE_MONTH,
  );
}

export function deriveTreatmentPhase(input: {
  treatmentState: string | null;
  firstActivationAt: Date | null;
  now?: Date;
}): TreatmentPhase {
  const now = input.now ?? new Date();
  if (input.treatmentState === "ended") return "ended";
  if (input.treatmentState === "paused") return "paused";
  if (!input.firstActivationAt || input.treatmentState === "onboarding") {
    return "pre_treatment";
  }
  const months =
    (now.getTime() - input.firstActivationAt.getTime()) / MS_PER_PHASE_MONTH;
  if (months < START_PHASE_MONTHS) return "start_phase";
  if (months < DOSE_ADJUSTMENT_MONTHS) return "dose_adjustment_phase";
  if (months < ACTIVE_WEIGHT_LOSS_MONTHS) return "active_weight_loss";
  if (months < STABILISATION_MONTHS) return "stabilisation";
  return "maintenance";
}

// --- derived: medical cycle day ---------------------------------------------
// today − active cycle start + 1, in whole Stockholm calendar days. TES-1596:
// both ends are the Swedish date, never the UTC date (which is yesterday
// between local midnight and 01:00/02:00) and never the server's zone. Null
// without an active cycle.
export function deriveCurrentMedicalCycleDay(
  activeCycleStartsAt: Date | null,
  now: Date = new Date(),
): number | null {
  if (!activeCycleStartsAt) return null;
  const swedishDay = (d: Date) =>
    Date.parse(`${stockholmDay(d.toISOString())}T00:00:00Z`);
  const diffDays = Math.round(
    (swedishDay(now) - swedishDay(activeCycleStartsAt)) / 86_400_000,
  );
  return diffDays >= 0 ? diffDays + 1 : null;
}
